import { useEffect, useState } from "react";
import { RouteFactory } from "@app/routePaths";
import { useGetShareCount } from "@features/pulse/hooks/useGetShareCount";
import { useHookUpdatePulseUrl } from "@features/pulse/hooks/useHookUpdatePulseUrl";
import useSendPulseGaData from "@features/pulse/hooks/useSendPulseGaData";
import {
  PulseArticleObject,
  SanitizedPulseData,
} from "@features/pulse/utils/types";
import { useIsFirstMount } from "@hooks/useIsFirstMount";
import useSendChartbeatViritualPageData from "@hooks/useSendChartbeatViritualPageData";
import { LIVE_DOMAIN } from "@util/constant";

/**
 * This hook sets the sorted pulse articles and individual pulse articles.
 * It sorts the articles based on the provided pulseArticles,
 * and updates the URL to reflect the first article's ID.
 */
export const useSetPulseArticle = ({ pulseArticles }: PulseArticleObject) => {
  const isFirstMount = useIsFirstMount();
  const [sortedPulseArticles, setSortedPulseArticles] = useState<
    SanitizedPulseData[]
  >([]);

  const { getShareCount } = useGetShareCount({});
  const { updatePulseUrl } = useHookUpdatePulseUrl();
  const { sendTrackingData } = useSendPulseGaData();

  const { sendChartbeatData } = useSendChartbeatViritualPageData();

  useEffect(() => {
    if (!isFirstMount) return;
    if (pulseArticles.length === 0) return;
    if (sortedPulseArticles.length > 0) return;

    const fetchShareCount = async () => {
      const shareData = await getShareCount(
        pulseArticles.map((article) => article.id)
      );

      const sortedPulseArticles = pulseArticles.map((article) => ({
        ...article,
        shareCount: shareData?.[article.id] || 0,
      }));

      setSortedPulseArticles(sortedPulseArticles);
    };

    fetchShareCount();
    updatePulseUrl(pulseArticles[0].id, true);

    const firstArticle = pulseArticles?.[0];

    const { title, id: activeId, sections } = firstArticle;
    const virtualUrl = RouteFactory.pulseSlide(activeId);

    const pagination = `1/${pulseArticles.length}`;
    sendTrackingData(title, virtualUrl, activeId, pulseArticles, pagination);

    sendChartbeatData({
      url: `${LIVE_DOMAIN}${virtualUrl}`,
      title,
      sections: sections,
    });
  }, [
    isFirstMount,
    sortedPulseArticles,
    pulseArticles,
    updatePulseUrl,
    sendTrackingData,
    sendChartbeatData,
    getShareCount,
  ]);

  return { sortedPulseArticles };
};
